import { Box, Button, Flex, Group, Text, Title } from '@mantine/core'
import {
  IconArrowRight,
  IconBolt,
  IconBorderCorners,
  IconChevronLeft,
  IconCpu2,
  IconFolder,
  IconVideo
} from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import { ModelTypeChip } from '@/components/models/ModelTypeChip/ModelTypeChip'
import { ApplicationType } from '@/types/app'
import { NormalizedStreamBusinessLogic } from '@/types/businessLogic'
import { CameraStreamWithDeviceId } from '@/types/camera'
import {
  DeploymentPlan,
  EventEndpoint,
  InputSourceType,
  MediaFolderConfig
} from '@/types/deployment'
import { Device } from '@/types/device'
import { MLModel } from '@/types/model'
import { IntegrationPreviewCard } from '../IntegrationSelection/IntegrationPreviewCard/IntegrationPreviewCard'
import { BusinessLogicName } from '../LogicSelection/BusinessLogicName'
import { getPlanErrorKind } from '../helpers/planError'
import { DeployPlanSection } from './DeployPlanSection/DeployPlanSection'
import { PreviewCard } from './PreviewCard'

const Arrow = () => (
  <Flex h={120} direction="column" align="center" justify="center">
    <IconArrowRight size={24} />
  </Flex>
)

type DeployPreviewProps = {
  applicationType: ApplicationType
  primaryModel: MLModel | null
  secondaryModel: MLModel | null
  cameraStreams: CameraStreamWithDeviceId[]
  savedStreamLogics: NormalizedStreamBusinessLogic[]
  savedEventEndpoints: EventEndpoint[]
  inputSource: InputSourceType
  selectedDeviceIds: string[]
  mediaFolderConfig: MediaFolderConfig
  runInGroups: boolean
  plan?: DeploymentPlan
  isPlanLoading: boolean
  planError: unknown
  devices?: Device[]
  onRefreshPlan: () => void
  onDeployModel: () => void
  onGoBack: () => void
}

export const DeployPreview = ({
  applicationType,
  primaryModel,
  secondaryModel,
  cameraStreams,
  savedStreamLogics,
  savedEventEndpoints,
  inputSource,
  selectedDeviceIds,
  mediaFolderConfig,
  runInGroups,
  plan,
  isPlanLoading,
  planError,
  devices,
  onRefreshPlan,
  onDeployModel,
  onGoBack
}: DeployPreviewProps) => {
  const isMediaFolder = inputSource === InputSourceType.MediaFolder
  const isMultiStage = applicationType === ApplicationType.MultiStage
  const showPlan = runInGroups && !isMediaFolder
  const isPlanRejected = showPlan && getPlanErrorKind(planError) === 'invalid'

  const uniqueLogicTypes = [
    ...new Set(
      savedStreamLogics
        .filter((l) => l.parameters.length > 0)
        .map((l) => l.logic_type)
    )
  ]

  return (
    <>
      <Title order={3} mb="md">
        <FormattedMessage id="deployments.step8" />
      </Title>

      <Box pos="relative">
        <StepSection
          actions={
            <Group>
              <Button
                size="md"
                variant="outline"
                miw={200}
                leftSection={<IconChevronLeft size={16} stroke={3} />}
                onClick={onGoBack}
              >
                <FormattedMessage id="deployments.previousStep" />
              </Button>

              <Button
                size="md"
                miw={200}
                disabled={isPlanRejected}
                onClick={onDeployModel}
              >
                <FormattedMessage
                  id="deployments.deployModel"
                  values={{ count: secondaryModel ? 2 : 1 }}
                />
              </Button>
            </Group>
          }
        >
          <Group align="center" mb="xl">
            {primaryModel && (
              <PreviewCard
                icon={<IconBolt size={24} />}
                title={primaryModel.name}
                content={
                  isMultiStage ? (
                    <ModelTypeChip modelType={primaryModel.model_type} />
                  ) : undefined
                }
              />
            )}

            <Arrow />

            {secondaryModel && (
              <>
                <PreviewCard
                  icon={<IconBolt size={24} />}
                  title={secondaryModel.name}
                  content={
                    isMultiStage ? (
                      <ModelTypeChip modelType={secondaryModel.model_type} />
                    ) : undefined
                  }
                />

                <Arrow />
              </>
            )}

            <PreviewCard
              icon={<IconCpu2 size={24} />}
              title={
                <FormattedMessage
                  id="deployments.preview.deviceCount"
                  values={{ count: selectedDeviceIds.length }}
                />
              }
            />

            <Arrow />

            {isMediaFolder ? (
              <PreviewCard
                icon={<IconFolder size={24} />}
                title={
                  <FormattedMessage id="deployments.preview.mediaFolder" />
                }
                content={
                  <Box w="100%">
                    <Text
                      size="xs"
                      c="dimmed"
                      ta="center"
                      title={mediaFolderConfig.input_folder_path}
                      truncate
                    >
                      {mediaFolderConfig.input_folder_path}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      ta="center"
                      title={mediaFolderConfig.output_folder_path}
                      truncate
                    >
                      {mediaFolderConfig.output_folder_path}
                    </Text>
                  </Box>
                }
              />
            ) : (
              <>
                <PreviewCard
                  icon={<IconVideo size={24} />}
                  title={
                    <FormattedMessage
                      id="deployments.preview.streamCount"
                      values={{ count: cameraStreams.length }}
                    />
                  }
                />

                <Arrow />

                {/* We currently support only one logic per deployment */}
                <PreviewCard
                  icon={<IconBorderCorners size={24} />}
                  title={
                    uniqueLogicTypes.length > 0 ? (
                      <BusinessLogicName
                        businessLogicType={uniqueLogicTypes[0]}
                      />
                    ) : (
                      <FormattedMessage id="deployments.preview.noLogicSelected" />
                    )
                  }
                />

                <Arrow />

                <IntegrationPreviewCard eventEndpoints={savedEventEndpoints} />
              </>
            )}
          </Group>

          {showPlan && (
            <DeployPlanSection
              plan={plan}
              isLoading={isPlanLoading}
              error={planError}
              devices={devices}
              onRefresh={onRefreshPlan}
            />
          )}
        </StepSection>
      </Box>
    </>
  )
}
